import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Alert,
  Chip,
  Paper,
  Stack,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { Refresh, AccountBalanceWallet, History as HistoryIcon, DeleteForever, Stop } from "@mui/icons-material";
import { useDataFetch } from "../../core/hooks/useDataFetch";
import { useResponsive } from "../../core/hooks/useResponsive";
import { Config } from "../../config/config";
import { formatSessionDate } from "./ptFormat";
import CurrentSessionCard from "./components/currentSessionCard";
import PaperTradeHistoryGrid from "./components/paperTradeHistoryGrid";

/** Panel chrome shared by both sections — matches the Dashboard / Backtest screens. */
const Section = ({ icon: Icon, title, caption, action, children }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: `1px solid ${alpha(primary, 0.14)}`,
        boxShadow: `0 8px 28px ${alpha("#000", 0.05)}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          px: { xs: 1.25, md: 1.5 },
          py: 0.75,
          bgcolor: alpha(primary, 0.06),
          borderBottom: `1px solid ${alpha(primary, 0.1)}`,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: alpha(primary, 0.14),
            color: primary,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} letterSpacing="-0.02em" noWrap>
            {title}
          </Typography>
          {caption && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {caption}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      <Box sx={{ p: { xs: 1, md: 1.25 } }}>{children}</Box>
    </Paper>
  );
};

const ManagePaperTrade = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { callAPI } = useDataFetch();
  const { isPhone } = useResponsive();
  const callAPIRef = useRef(callAPI);
  callAPIRef.current = callAPI;

  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [lastMessage, setLastMessage] = useState(null); // { type: 'success'|'error', text }

  async function loadAll() {
    setRefreshing(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        callAPIRef.current(Config.ITradeAPI.getPaperTradeStatus),
        callAPIRef.current(Config.ITradeAPI.getPaperTradeHistory),
      ]);
      if (statusRes.status) setStatus(statusRes.data);
      if (historyRes.status) setHistory(historyRes.data?.history || []);
      setLoaded(true);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  const runAction = async (endpoint, fallbackError) => {
    setBusy(true);
    setLastMessage(null);
    const res = await callAPIRef.current(endpoint);
    if (res.status) {
      setLastMessage({ type: "success", text: res.data?.message || "Done." });
      await loadAll();
    } else {
      setLastMessage({ type: "error", text: res.error || fallbackError });
    }
    setBusy(false);
  };

  const handleCloseConfirmed = async () => {
    setConfirmCloseOpen(false);
    await runAction(Config.ITradeAPI.closePaperTrade, "Failed to close paper trade session.");
  };

  const handleDeleteConfirmed = async () => {
    setConfirmDeleteOpen(false);
    await runAction(Config.ITradeAPI.deletePaperTradeSession, "Failed to delete the current paper trade session.");
  };

  if (!loaded) {
    return (
      <Box sx={{ p: { xs: 1, md: 1.5 }, maxWidth: 1400, mx: "auto" }}>
        <Skeleton variant="rounded" height={isPhone ? 30 : 36} width={220} sx={{ mb: 1.5, borderRadius: 1 }} />
        <Skeleton variant="rounded" height={isPhone ? 260 : 300} sx={{ mb: 1.5, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={isPhone ? 200 : 320} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const openCount = status?.current?.openPositions ?? 0;

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: alpha(primary, 0.03),
        pb: 2,
        pt: { xs: 0.75, md: 1 },
        px: { xs: 0.75, sm: 1.5, md: 2 },
        position: "relative",
      }}
    >
      {(refreshing || busy) && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, zIndex: 10 }}
        />
      )}

      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
          <Typography variant={isPhone ? "subtitle1" : "h6"} fontWeight={800} sx={{ letterSpacing: "-0.01em" }} noWrap>
            Manage Paper Trade
          </Typography>
          <Button
            variant="contained"
            size={isPhone ? "small" : "medium"}
            startIcon={<Refresh />}
            onClick={() => loadAll()}
            disabled={refreshing || busy}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              px: { xs: 1.25, sm: 2 },
              minWidth: { xs: "auto", sm: 112 },
              flexShrink: 0,
              boxShadow: `0 6px 20px ${alpha(primary, 0.25)}`,
            }}
          >
            Refresh
          </Button>
        </Stack>

        {lastMessage && (
          <Alert severity={lastMessage.type} sx={{ mb: 1, py: 0, "& .MuiAlert-message": { py: 0.6 } }} onClose={() => setLastMessage(null)}>
            {lastMessage.text}
          </Alert>
        )}

        <Stack spacing={1.25}>
          <Section
            icon={AccountBalanceWallet}
            title="Current session"
            caption={
              status?.current
                ? `Session started ${formatSessionDate(status.current.startDate)}`
                : "Nothing traded yet"
            }
            action={
              status?.current ? (
                <Chip
                  size="small"
                  label={openCount > 0 ? `${openCount} open` : "Flat"}
                  color={openCount > 0 ? "warning" : "success"}
                  variant={openCount > 0 ? "filled" : "outlined"}
                  sx={{ fontWeight: 700, flexShrink: 0 }}
                />
              ) : null
            }
          >
            <CurrentSessionCard
              status={status}
              busy={busy}
              onClose={() => setConfirmCloseOpen(true)}
              onDelete={() => setConfirmDeleteOpen(true)}
            />
          </Section>

          <Section
            icon={HistoryIcon}
            title="Paper trade history"
            caption={`${history.length} closed session${history.length === 1 ? "" : "s"}`}
          >
            <PaperTradeHistoryGrid history={history} />
          </Section>
        </Stack>
      </Box>

      {/* Close — archives the session into history */}
      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
          <Stop color="primary" /> Close Paper Trade?
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            This will summarize the current session into Paper Trade History, clear the paper trade
            book, and immediately start a new session. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCloseOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleCloseConfirmed} sx={{ textTransform: "none", fontWeight: 700 }}>
            Close Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete — discards the session, writes nothing to history */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 800 }}>
          <DeleteForever color="error" /> Delete Current Session?
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2" sx={{ mb: 1.5 }}>
            This permanently clears every trade in the current paper trade book
            {openCount > 0 ? `, including ${openCount} open position${openCount === 1 ? "" : "s"}` : ""}.
          </DialogContentText>
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Nothing is written to Paper Trade History — this run leaves no record. Use{" "}
            <strong>Close Paper Trade</strong> instead if you want it archived.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed} sx={{ textTransform: "none", fontWeight: 700 }}>
            Delete Session
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePaperTrade;
